import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Modal, Form, Input, message, Popconfirm, Select, Divider } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SettingOutlined, SearchOutlined } from '@ant-design/icons';
import { categoryAPI, freightAPI } from '../api';
import MultiLevelJsonEditor from './Utils/MultiLevelJsonEditor';

const CategoryManagement = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [freightModalVisible, setFreightModalVisible] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [freightChannels, setFreightChannels] = useState([]);
  const [selectedFreightChannels, setSelectedFreightChannels] = useState([]);
  const [batchLoading, setBatchLoading] = useState(false);
  const [multiLevelData, setMultiLevelData] = useState({});
  const [multiLevelLoading, setMultiLevelLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });
  const [searchText, setSearchText] = useState('');
  const [form] = Form.useForm();

  useEffect(() => {
    fetchCategories();
    fetchFreightChannels();
    fetchMultiLevelData();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await categoryAPI.getAll();
      setCategories(response.data);
    } catch (error) {
      message.error('获取分类列表失败: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const fetchFreightChannels = async () => {
    try {
      const response = await freightAPI.getAll();
      setFreightChannels(response.data);
    } catch (error) {
      message.error('获取物流渠道失败: ' + (error.response?.data?.detail || error.message));
    }
  };

  // 转换旧格式数据：将纯数字键转换为 "cat_" 前缀格式
  const migrateDataFormat = (data) => {
    const newData = {};
    Object.entries(data).forEach(([key, value]) => {
      if (typeof value === 'number') {
        // 叶子节点：如果键是纯数字，转换为 "cat_数字" 格式
        const newKey = /^\d+$/.test(key) ? `cat_${key}` : key;
        newData[newKey] = value;
      } else if (typeof value === 'object' && value !== null) {
        // 非叶子节点：递归处理
        newData[key] = migrateDataFormat(value);
      } else {
        newData[key] = value;
      }
    });
    return newData;
  };

  const fetchMultiLevelData = async () => {
    try {
      const response = await categoryAPI.getMultiLevel();
      let data = response.data.data || {};
      // 自动迁移旧格式数据
      data = migrateDataFormat(data);
      setMultiLevelData(data);
    } catch (error) {
      message.error('获取多级分类数据失败: ' + (error.response?.data?.detail || error.message));
    }
  };

  // 递归查找ID在树中的路径
  const findIdPathsInTree = (data, idToFind, currentPath = []) => {
    const paths = [];
    
    for (const [key, value] of Object.entries(data)) {
      const newPath = [...currentPath, key];
      
      if (typeof value === 'number') {
        // 叶子节点，检查是否匹配
        if (value === idToFind) {
          paths.push(newPath.join(' > '));
        }
      } else if (typeof value === 'object' && value !== null) {
        // 递归查找
        const subPaths = findIdPathsInTree(value, idToFind, newPath);
        paths.push(...subPaths);
      }
    }
    
    return paths;
  };

  const saveMultiLevelData = async () => {
    try {
      setMultiLevelLoading(true);
      await categoryAPI.updateMultiLevel(multiLevelData);
      
      // 保存成功，弹出alert提示
      window.alert('✅ 多级分类数据保存成功！');
      message.success('多级分类数据保存成功');
    } catch (error) {
      // 检查是否有结构化的错误信息
      const errorDetail = error.response?.data?.detail;
      
      if (errorDetail && typeof errorDetail === 'object' && errorDetail.invalid_ids) {
        // 后端返回了无效的ID列表
        const invalidIds = errorDetail.invalid_ids;
        const errorMessages = [];
        
        for (const invalidId of invalidIds) {
          const paths = findIdPathsInTree(multiLevelData, invalidId);
          if (paths.length > 0) {
            errorMessages.push(`ID ${invalidId} 在以下位置:\n  ${paths.join('\n  ')}`);
          } else {
            errorMessages.push(`ID ${invalidId} (未找到路径)`);
          }
        }
        
        const alertMessage = `❌ 保存失败！以下分类ID不存在:\n\n${errorMessages.join('\n\n')}\n\n请清理这些不存在的分类后再保存。`;
        window.alert(alertMessage);
        message.error('保存失败：存在无效的分类ID');
      } else {
        // 其他类型的错误
        const errorMsg = typeof errorDetail === 'string' 
          ? errorDetail 
          : (errorDetail?.message || error.message);
        window.alert(`❌ 保存失败！\n\n${errorMsg}`);
        message.error('保存多级分类数据失败: ' + errorMsg);
      }
      
      throw error; // 重新抛出错误，让MultiLevelJsonEditor知道保存失败
    } finally {
      setMultiLevelLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingCategory(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingCategory(record);
    form.setFieldsValue(record);
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await categoryAPI.delete(id);
      message.success('删除成功');
      fetchCategories();
    } catch (error) {
      message.error('删除失败: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleSubmit = async (values) => {
    try {
      if (editingCategory) {
        await categoryAPI.update(editingCategory.id, values);
        message.success('更新成功');
      } else {
        await categoryAPI.create(values);
        message.success('创建成功');
      }
      setModalVisible(false);
      fetchCategories();
    } catch (error) {
      message.error((editingCategory ? '更新失败' : '创建失败') + ': ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleBatchSetFreight = (record) => {
    setSelectedCategory(record);
    setSelectedFreightChannels([]);
    setFreightModalVisible(true);
  };

  const handleFreightSubmit = async () => {
    if (!selectedCategory || selectedFreightChannels.length === 0) {
      message.warning('请选择至少一个物流渠道');
      return;
    }

    try {
      setBatchLoading(true);
      
      
      const response = await categoryAPI.batchSetFreightChannels(selectedCategory.id, selectedFreightChannels);
      
      
      message.success(`成功为分类"${selectedCategory.name}"下的 ${response.data.updated_count} 个商品设置物流渠道`);
      setFreightModalVisible(false);
      setSelectedCategory(null);
      setSelectedFreightChannels([]);
    } catch (error) {
      
      message.error(`批量设置物流渠道失败: ${error.response?.data?.detail || error.message}`);
    } finally {
      setBatchLoading(false);
    }
  };

  const handleTableChange = (paginationConfig) => {
    setPagination({
      current: paginationConfig.current,
      pageSize: paginationConfig.pageSize,
    });
  };

  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
    // 重置到第一页
    setPagination({
      ...pagination,
      current: 1,
    });
  };

  const getFilteredCategories = () => {
    if (!searchText.trim()) {
      return categories;
    }
    
    const lowerSearchText = searchText.toLowerCase();
    return categories.filter(category => {
      const nameMatch = category.name?.toLowerCase().includes(lowerSearchText);
      const descriptionMatch = category.description?.toLowerCase().includes(lowerSearchText);
      return nameMatch || descriptionMatch;
    });
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: '8%',
    },
    {
      title: '分类名称(最终商品分类)',
      dataIndex: 'name',
      key: 'name',
      width: '20%',
      ellipsis: true,
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      width: '25%',
      ellipsis: true,
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => new Date(date).toLocaleString('zh-CN'),
      width: '17%',
    },
    {
      title: '操作',
      key: 'action',
      width: '30%',
      render: (_, record) => (
        <Space size="small" wrap>
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Button
            type="default"
            size="small"
            icon={<SettingOutlined />}
            onClick={() => handleBatchSetFreight(record)}
            style={{
              borderColor: '#52c41a',
              color: '#52c41a'
            }}
          >
            设物流渠道
          </Button>
          <Popconfirm
            title="确定要删除这个分类吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确认"
            cancelText="取消"
          >
            <Button
              type="primary"
              danger
              size="small"
              icon={<DeleteOutlined />}
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const filteredCategories = getFilteredCategories();

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 'bold' }}>分类管理</h1>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleAdd}
        >
          添加最终商品分类
        </Button>
      </div>

      <div style={{ marginBottom: 16 }}>
        <Input
          placeholder="搜索分类名称或描述..."
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={handleSearchChange}
          allowClear
          style={{ width: 400 }}
        />
        {searchText && (
          <span style={{ marginLeft: 12, color: '#888' }}>
            找到 {filteredCategories.length} 条结果
          </span>
        )}
      </div>

      <Table
        columns={columns}
        dataSource={filteredCategories}
        rowKey="id"
        loading={loading}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total) => `共 ${total} 条记录`,
          total: filteredCategories.length,
        }}
        onChange={handleTableChange}
        size="middle"
      />

      <Modal
        title={editingCategory ? '编辑最终商品分类' : '添加最终商品分类'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        okText="确认"
        cancelText="取消"
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            label="分类名称"
            name="name"
            rules={[
              { required: true, message: '请输入分类名称' },
              { max: 50, message: '分类名称不能超过50个字符' },
            ]}
          >
            <Input placeholder="请输入分类名称" />
          </Form.Item>

          <Form.Item
            label="描述"
            name="description"
            rules={[
              { max: 200, message: '描述不能超过200个字符' },
            ]}
          >
            <Input.TextArea
              placeholder="请输入分类描述"
              rows={4}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`批量设置物流渠道 - ${selectedCategory?.name}`}
        open={freightModalVisible}
        onCancel={() => {
          setFreightModalVisible(false);
          setSelectedCategory(null);
          setSelectedFreightChannels([]);
        }}
        onOk={handleFreightSubmit}
        okText="确认设置"
        cancelText="取消"
        confirmLoading={batchLoading}
        width={600}
      >
        <div style={{ marginBottom: 16 }}>
          <p style={{ color: '#666', marginBottom: 16 }}>
            此操作将为分类 <strong>"{selectedCategory?.name}"</strong> 下的所有商品统一设置支持的物流渠道。
          </p>
          <p style={{ color: '#ff4d4f', fontSize: '12px', marginBottom: 16 }}>
            ⚠️ 注意：此操作将覆盖该分类下所有商品现有的物流渠道配置
          </p>
        </div>
        
        <Form layout="vertical">
          <Form.Item
            label="选择物流渠道"
            required
          >
            <Select
              mode="multiple"
              placeholder="请选择要设置的物流渠道"
              value={selectedFreightChannels}
              onChange={setSelectedFreightChannels}
              style={{ width: '100%' }}
              showSearch
              filterOption={(input, option) =>
                (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
              }
            >
              {freightChannels.map(channel => (
                <Select.Option key={channel.id} value={channel.id}>
                  {channel.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Form>

        {selectedFreightChannels.length > 0 && (
          <div style={{ 
            backgroundColor: '#f6ffed', 
            border: '1px solid #b7eb8f', 
            borderRadius: '6px', 
            padding: '12px', 
            marginTop: '16px' 
          }}>
            <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
              已选择 {selectedFreightChannels.length} 个物流渠道：
            </div>
            <div>
              {selectedFreightChannels.map(channelId => {
                const channel = freightChannels.find(c => c.id === channelId);
                return channel ? (
                  <span key={channelId} style={{ 
                    display: 'inline-block', 
                    backgroundColor: '#52c41a', 
                    color: 'white', 
                    padding: '2px 8px', 
                    borderRadius: '4px', 
                    margin: '2px 4px 2px 0',
                    fontSize: '12px'
                  }}>
                    {channel.name}
                  </span>
                ) : null;
              })}
            </div>
          </div>
        )}
      </Modal>

      {/* 分类选择对话框 */}
      <Modal
        title="批量设置物流渠道"
        open={freightModalVisible}
        onCancel={() => {
          setFreightModalVisible(false);
          setSelectedCategory(null);
          setSelectedFreightChannels([]);
        }}
        onOk={handleFreightSubmit}
        okText="确认设置"
        cancelText="取消"
        confirmLoading={batchLoading}
        width={600}
      >
        <div style={{ marginBottom: 16 }}>
          <p style={{ color: '#666', marginBottom: 16 }}>
            此操作将为分类 <strong>"{selectedCategory?.name}"</strong> 下的所有商品统一设置支持的物流渠道。
          </p>
          <p style={{ color: '#ff4d4f', fontSize: '12px', marginBottom: 16 }}>
            ⚠️ 注意：此操作将覆盖该分类下所有商品现有的物流渠道配置
          </p>
        </div>
        
        <Form layout="vertical">
          <Form.Item
            label="选择物流渠道"
            required
          >
            <Select
              mode="multiple"
              placeholder="请选择要设置的物流渠道"
              value={selectedFreightChannels}
              onChange={setSelectedFreightChannels}
              style={{ width: '100%' }}
              showSearch
              filterOption={(input, option) =>
                (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
              }
            >
              {freightChannels.map(channel => (
                <Select.Option key={channel.id} value={channel.id}>
                  {channel.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Form>

        {selectedFreightChannels.length > 0 && (
          <div style={{ 
            backgroundColor: '#f6ffed', 
            border: '1px solid #b7eb8f', 
            borderRadius: '6px', 
            padding: '12px', 
            marginTop: '16px' 
          }}>
            <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
              已选择 {selectedFreightChannels.length} 个物流渠道：
            </div>
            <div>
              {selectedFreightChannels.map(channelId => {
                const channel = freightChannels.find(c => c.id === channelId);
                return channel ? (
                  <span key={channelId} style={{ 
                    display: 'inline-block', 
                    backgroundColor: '#52c41a', 
                    color: 'white', 
                    padding: '2px 8px', 
                    borderRadius: '4px', 
                    margin: '2px 4px 2px 0',
                    fontSize: '12px'
                  }}>
                    {channel.name}
                  </span>
                ) : null;
              })}
            </div>
          </div>
        )}
      </Modal>

      <Divider />

      {/* 多级分类编辑器 */}
      <MultiLevelJsonEditor
        data={multiLevelData}
        onDataChange={setMultiLevelData}
        onSave={saveMultiLevelData}
        loading={multiLevelLoading}
        title="多级分类编辑"
        keyPrefix="cat_"
        items={categories}
        itemIdField="id"
        itemNameField="name"
        leafNodeSuffix="(最终类别)"
        addTopLevelText="添加顶级分类"
        addTopLevelLeafText="添加最终商品分类作为顶级类别"
        selectModalTitle="选择要添加的商品分类"
        selectLabel="商品分类"
        selectPlaceholder="请选择商品分类"
      />
    </div>
  );
};

export default CategoryManagement;
